/* UI */
import { Box, Card } from '@auviknetworks/iceberg-ui';
import { Divider, Typography } from '@mui/material';

/*  Shared  */
import {
    AppNameLinkCell,
    HMSCell,
    PercentageCell,
    UsageChartCell
} from 'application/Shared/Components/Cells';
import {
    Header,
    ManagedTableV2ContextProvider,
    ManagedTableV2UI
} from 'application/Shared/Components/Table';
import { sortByRanking } from 'application/Shared/Components/Table/tableSortingFunctions';
import {
    CSVColumn,
    ColumnDef
} from 'application/Shared/Components/Table/types';
import { TABLENAMES } from 'application/Shared/Constants';
import { Application as ApplicationType } from 'application/Shared/types/application';
import { TenantApp } from 'application/Shared/types/tenantApp';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';

interface UsageBreakdownType {
    application: ApplicationType;
    id: string;
    link: TenantApp;
    name: string;
    percent: number;
    position: number;
    usage: number;
}

const columns: ColumnDef<UsageBreakdownType, string>[] = [
    {
        accessorKey: 'application.name',
        cell: (props) => <AppNameLinkCell {...props} />,
        enableColumnFilter: false,
        enableGlobalFilter: true,
        header: Header,
        id: 'apps',
        name: 'Common.application',
        size: 300,
        testId: 'application'
    },
    {
        accessorKey: 'usage',
        cell: HMSCell,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        header: Header,
        id: 'usage',
        name: 'Common.usage',
        size: 100,
        testId: 'usage'
    },
    {
        accessorKey: 'percent',
        cell: PercentageCell,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        header: Header,
        id: 'usagePercentage',
        name: 'Common.percentage',
        size: 100,
        testId: 'usagePercentage'
    },
    {
        accessorKey: 'link',
        cell: UsageChartCell,
        enableColumnFilter: false,
        enableSorting: false,
        header: Header,
        id: 'usageChart',
        name: 'Common.usage_chart',
        size: 150,
        sortingFn: sortByRanking,
        testId: 'usageChart'
    }
];

export const UsageBreakdown = ({
    renderingScheduledReport,
    reportData
}: {
    renderingScheduledReport: boolean;
    reportData: UsageBreakdownType[];
}) => {
    const { t } = useTranslation();

    const csvExportFields: CSVColumn[] = [
        {
            key: 'application.name',
            label: t('Common.app_name')
        },
        {
            key: 'application.category',
            label: t('Common.applicationCategory')
        },
        {
            key: 'application.subCategory',
            label: t('Common.applicationSubcategory')
        },
        {
            key: 'usage',
            label: t('Common.usage'),
            transform: 'hms'
        },
        {
            key: 'percent',
            label: t('Common.percentage')
        }
    ];

    const rowsPerPage = renderingScheduledReport ? -1 : 10;

    return (
        <Box marginY="24px" marginTop="50px">
            <Typography variant="h5">
                {t('Reports.UsageReport.usage_breakdown')}
            </Typography>
            <Divider
                sx={{
                    borderBottomWidth: '4px',
                    borderColor: 'border.darkerDivider',
                    marginBottom: '16px',
                    marginTop: '8px'
                }}
            />
            <ManagedTableV2ContextProvider
                columns={columns}
                ignoreSearchParams
                mode="client"
                rows={reportData}
                rowsPerPage={rowsPerPage}
                searchEnabled
                sortDirection="DESC"
                sortKey="usage"
                tableName={TABLENAMES.USAGE_BREAKDOWN}
            >
                <Card sx={{ marginBottom: '32px' }}>
                    {/*
                                border radius fixes visual bug where the table doesn't have rounded corners
                                and causes a break in the bottom corners of the Card's border that contains it
                                TODO: Maybe there's a better way to fix this???
                            */}
                    <Box
                        sx={{ borderRadius: '0 0 8px 8px', overflow: 'hidden' }}
                    >
                        <ManagedTableV2UI
                            csvExport
                            csvExportFields={csvExportFields}
                            csvExportFileName="usage_top_applications"
                            renderFooter
                            titleDef={{
                                titleKey: 'Common.applications'
                            }}
                        />
                    </Box>
                </Card>
            </ManagedTableV2ContextProvider>
        </Box>
    );
};
